import { useEffect, useState, useEffectEvent, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthService } from "@domain/services/auth.service";
import { ServiceMetadataService } from "@domain/services/serviceMetadata.service";
import { OrderStatusConfigService } from "@domain/services/orderStatusConfig.service";
import { preloadYandexMaps } from "@boundary/_hooks/useYandexMaps";
import { prefetchAndNavigate } from "@boundary/utils/prefetchRoute";
import type { Auth } from "@infrastructure/stores/auth.store";
import { getStartParam } from "@shared/integrations/telegram/init";
import { CreditCardService } from "@domain/services/creditCard.service";
import { MachineStore } from "@infrastructure/stores/machine.store";
import { isDemoSession } from "@shared/integrations/demoLogin";

const MIN_DISPLAY_TIME_MS = 0;

interface NavigationResult {
  route: string;
  // Если задан, сначала navigate на этот путь (для создания истории)
  historyRoute?: string;
}

/**
 * Определяет маршруты при возврате после привязки карты
 * Создает стек навигации для корректной работы кнопки "назад"
 */
function getRoutesFromBindingContext(): NavigationResult | null {
  const context = CreditCardService.getAndClearBindingContext();
  if (!context) return null;
  
  console.log(`[getRoutesFromBindingContext] Found context:`, context);
  
  // Восстанавливаем выбранную машину
  if (context.machineId) {
    console.log(`[getRoutesFromBindingContext] Restoring machineId=${context.machineId}`);
    MachineStore.selectMachine(context.machineId);
  }
  
  const machineId = context.machineId || 1;
  const machineParam = `?machineId=${machineId}`;
  
  // Используем сохраненный backPath для создания истории
  // returnPath - куда вернуться, backPath - куда ведёт кнопка "назад"
  const returnPath = context.returnPath.startsWith("/payment/settings")
    ? `/payment${machineParam}` // Всегда возвращаем на /payment, не на /payment/settings
    : context.returnPath.includes("machineId")
      ? context.returnPath
      : `${context.returnPath}${context.returnPath.includes("?") ? "&" : "?"}machineId=${machineId}`;
  
  return {
    historyRoute: context.backPath,
    route: returnPath,
  };
}

function getInitialRoute(authState: Auth.State): NavigationResult {
  // Проверяем start_param для deep linking
  const startParam = getStartParam();
  
  console.log(`[getInitialRoute] startParam="${startParam}", authState="${authState.state}"`);
  console.log(`[getInitialRoute] window.location.href="${window.location.href}"`);
  console.log(`[getInitialRoute] window.location.hash="${window.location.hash}"`);
  
  // Логируем Telegram WebApp данные
  if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp) {
    const tg = (window as any).Telegram.WebApp;
    console.log(`[getInitialRoute] Telegram.WebApp.initDataUnsafe:`, JSON.stringify(tg.initDataUnsafe));
    console.log(`[getInitialRoute] Telegram.WebApp.initDataUnsafe.start_param:`, tg.initDataUnsafe?.start_param);
  }
  
  if (startParam === "cards" && authState.state === "authenticated") {
    // После привязки карты — проверяем сохраненный контекст
    const contextRoutes = getRoutesFromBindingContext();
    if (contextRoutes) {
      console.log(`[getInitialRoute] Returning to saved context with history stack`);
      return contextRoutes;
    }
    // Fallback: если контекста нет, создаем стек /cart -> /payment
    console.log(`[getInitialRoute] No context, creating default history stack`);
    return {
      historyRoute: "/cart?machineId=1",
      route: "/payment?machineId=1",
    };
  }
  
  // Стандартная логика: authenticated → /map, anonymous → /about
  if (authState.state === "authenticated") {
    console.log(`[getInitialRoute] No startParam match, going to /map`);
    return { route: "/map" };
  }
  // На показе человек входит по пропуску: если состояние ещё не обновилось, вести
  // его на «О нас» нельзя — он вошедший, просто приложение об этом пока не знает.
  if (isDemoSession()) {
    console.log(`[getInitialRoute] Вход по пропуску показа, going to /map`);
    return { route: "/map" };
  }

  // Любой anonymous (зарегистрирован или нет) → на /about
  // Переход на /login происходит только по кнопке с /about
  console.log(`[getInitialRoute] Anonymous user, going to /about`);
  return { route: "/about" };
}

export function useAppInitialization() {
  const navigate = useNavigate();
  const [startTime] = useState(() => Date.now());
  const hasInitialized = useRef(false);

  const handleRedirect = useEffectEvent(async () => {
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    console.log("[useAppInitialization] Starting AuthService.init()...");
    await AuthService.init();
    console.log("[useAppInitialization] AuthService.init() completed");

    ServiceMetadataService.loadServices().catch(() => {});
    OrderStatusConfigService.loadStatuses().catch(() => {});

    const store = AuthService.getStore();
    const state = store.getState();
    const authState = state.auth;

    const elapsed = Date.now() - startTime;
    const remainingTime = Math.max(0, MIN_DISPLAY_TIME_MS - elapsed);
    if (remainingTime > 0) {
      await new Promise((resolve) => setTimeout(resolve, remainingTime));
    }

    const navResult = getInitialRoute(authState);
    
    // Если есть historyRoute, сначала переходим туда для создания стека навигации
    // Это позволит кнопке "назад" работать корректно
    if (navResult.historyRoute) {
      console.log(`[useAppInitialization] Creating history entry: ${navResult.historyRoute}`);
      prefetchAndNavigate(navigate, navResult.historyRoute, { replace: true });
      // Небольшая задержка чтобы первый navigate успел обработаться
      await new Promise((resolve) => setTimeout(resolve, 50));
      console.log(`[useAppInitialization] Navigating to final route: ${navResult.route}`);
      prefetchAndNavigate(navigate, navResult.route);
    } else {
      prefetchAndNavigate(navigate, navResult.route, { replace: true });
    }
  });

  useEffect(() => {
    console.log("[useAppInitialization] useEffect triggered");
    preloadYandexMaps();
    
    // Инициализируем механизм фоновой переавторизации
    AuthService.initBackgroundReauth();
    
    console.log("[useAppInitialization] Calling handleRedirect...");
    void handleRedirect();
  }, [handleRedirect]);
}
